/**
 * @module utils/auditFormatters
 * @description Formateadores visuales y de texto para el trail de auditoría.
 *
 * Transforma entradas de auditoría crudas en representaciones visuales y textuales
 * listas para mostrar en el componente `AuditTrail` del DetailPanel.
 *
 * ## Exports principales
 * | Export | Tipo | Propósito |
 * |--------|------|-----------|
 * | `AUDIT_ICONS` | constante | Emoji por tipo de acción (insert, update, delete, etc.) |
 * | `AUDIT_COLORS` | constante | Clases Tailwind CSS de color por tipo de acción |
 * | `getAuditActionType()` | función | Clasifica un `AuditEntry.actionType` en `AuditActionType` |
 * | `formatAuditSummary()` | función | Genera resumen estadístico de múltiples entradas |
 * | `groupAuditByDate()` | función | Agrupa entradas por fecha para mostrar separadores |
 * | `filterAuditEntries()` | función | Filtra por texto, tipo de acción y/o usuario |
 * | `sortAuditEntries()` | función | Ordena por timestamp (más reciente primero por defecto) |
 * | `formatAuditEntryForExport()` | función | Serializa una entrada para CSV/PDF |
 *
 * @see auditExport.ts para exportar el trail a archivos
 * @see auditMessages.ts para los mensajes legibles de cada tipo de cambio
 * @see AuditEntry en types/iso.ts para la estructura de datos
 */

import { AuditEntry } from '../types/iso';

// =====================================================
// TIPOS Y CONSTANTES
// =====================================================

/**
 * Tipos de acciones de auditoría reconocidas
 */
export type AuditActionType =
  | 'insert'
  | 'update'
  | 'delete'
  | 'assign'
  | 'complete'
  | 'upload'
  | 'other';

/**
 * Configuración de iconos por tipo de acción
 */
export const AUDIT_ICONS: Record<AuditActionType, string> = {
  insert: '📝',
  update: '✏️',
  delete: '🗑️',
  assign: '👤',
  complete: '✅',
  upload: '📎',
  other: '📋'
};

/**
 * Configuración de colores por tipo de acción
 */
export const AUDIT_COLORS: Record<AuditActionType, string> = {
  insert: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
  update: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
  delete: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
  assign: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
  complete: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
  upload: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20',
  other: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20'
};

/**
 * Mapeo de nombres de campos técnicos a nombres legibles
 */
export const FIELD_NAMES: Record<string, string> = {
  // Campos de controles
  status: 'Estado',
  priority: 'Prioridad',
  due_date: 'Fecha de vencimiento',
  completion_date: 'Fecha de completado',
  title: 'Título',
  description: 'Descripción',

  // Campos de tareas
  assignee_id: 'Responsable',
  task_status: 'Estado de tarea',
  completed_at: 'Fecha de completado',
  task_title: 'Título de tarea',
  task_description: 'Descripción de tarea',
  estimated_hours: 'Horas estimadas',

  // Campos de evidencias
  evidence_name: 'Nombre de evidencia',
  evidence_type: 'Tipo de evidencia',
  file_name: 'Nombre de archivo',
  file_size: 'Tamaño de archivo',
  uploaded_by: 'Subido por',

  // Campos de asignaciones
  referent_id: 'Referente técnico',
  referent_name: 'Nombre del referente',
  assigned_to: 'Asignado a',
  assigned_by: 'Asignado por',

  // Campos genéricos
  created_at: 'Fecha de creación',
  updated_at: 'Última actualización',

  // Nombres de tablas
  iso_controls: 'Control ISO',
  tasks: 'Tarea',
  evidence: 'Evidencia',
  control_assignments: 'Asignación de Control',
  task_assignments: 'Asignación de Tarea'
};

/**
 * Mapeo de estados a texto legible con emojis
 */
export const STATUS_LABELS: Record<string, string> = {
  not_started: '⚪ No Iniciado',
  in_progress: '🔵 En Progreso',
  review: '🟡 En Revisión',
  completed: '🟢 Completado',
  non_compliant: '🔴 No Conforme',
  pending: '⏳ Pendiente'
};

/**
 * Mapeo de prioridades a texto legible con emojis
 */
export const PRIORITY_LABELS: Record<string, string> = {
  low: '🟢 Baja',
  medium: '🟡 Media',
  high: '🟠 Alta',
  critical: '🔴 Crítica'
};

// =====================================================
// FUNCIONES DE FORMATEO
// =====================================================

/**
 * Determina el tipo de acción basado en el texto de acción y detalles
 */
export function getActionType(action: string, details?: Record<string, any>): AuditActionType {
  const actionLower = action.toLowerCase();

  if (actionLower.includes('insert') || actionLower.includes('crear') || actionLower.includes('agregad')) {
    return 'insert';
  }
  if (actionLower.includes('update') || actionLower.includes('actualiz') || actionLower.includes('modificad')) {
    return 'update';
  }
  if (actionLower.includes('delete') || actionLower.includes('eliminar') || actionLower.includes('borrad')) {
    return 'delete';
  }
  if (actionLower.includes('assign') || actionLower.includes('asignad')) {
    return 'assign';
  }
  if (actionLower.includes('complet') || actionLower.includes('finaliz')) {
    return 'complete';
  }
  if (actionLower.includes('upload') || actionLower.includes('subid') || actionLower.includes('evidencia')) {
    return 'upload';
  }

  return 'other';
}

/**
 * Formatea una fecha en formato legible
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) return dateString;

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Hace menos de 1 minuto
    if (diffMins < 1) {
      return 'Hace un momento';
    }

    // Hace menos de 1 hora
    if (diffMins < 60) {
      return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
    }

    // Hace menos de 24 horas
    if (diffHours < 24) {
      return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    }

    // Hace menos de 7 días
    if (diffDays < 7) {
      return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    }

    // Formato completo
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Formatea un nombre de campo técnico a texto legible
 */
export function formatFieldName(fieldName: string): string {
  return FIELD_NAMES[fieldName] || fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^./, str => str.toUpperCase());
}

/**
 * Formatea un valor según su tipo y contenido
 */
export function formatValue(value: any, fieldName?: string): string {
  // Valor nulo o undefined
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Booleanos
  if (typeof value === 'boolean') {
    return value ? '✓ Sí' : '✗ No';
  }

  // Números
  if (typeof value === 'number') {
    return value.toLocaleString('es-ES');
  }

  // Fechas (detectar strings que parecen fechas ISO)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return formatDate(value);
  }

  // Estados
  if (fieldName === 'status' || fieldName === 'task_status') {
    return STATUS_LABELS[value] || value;
  }

  // Prioridades
  if (fieldName === 'priority') {
    return PRIORITY_LABELS[value] || value;
  }

  // Strings normales
  if (typeof value === 'string') {
    return value;
  }

  // Objetos
  if (typeof value === 'object') {
    // Si tiene una propiedad 'name', usarla
    if ('name' in value) {
      return String(value.name);
    }
    // Si tiene una propiedad 'title', usarla
    if ('title' in value) {
      return String(value.title);
    }
    // Si es un array
    if (Array.isArray(value)) {
      return `${value.length} elemento${value.length !== 1 ? 's' : ''}`;
    }
    // Intentar convertir a JSON
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[Objeto complejo]';
    }
  }

  return String(value);
}

/**
 * Parsea los detalles de auditoría y extrae información relevante
 */
export function parseAuditDetails(details: Record<string, any>): {
  tableName?: string;
  changedFields?: string[];
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  metadata?: Record<string, any>;
  parsedChanges: Array<{
    field: string;
    fieldLabel: string;
    oldValue: string;
    newValue: string;
  }>;
} {
  const result: any = {
    parsedChanges: []
  };

  // Extraer información conocida
  if (details.table_name || details['table name']) {
    result.tableName = details.table_name || details['table name'];
  }

  if (details.changed_fields || details['changed fields']) {
    const fields = details.changed_fields || details['changed fields'];
    result.changedFields = typeof fields === 'string' ? fields.split(',') : fields;
  }

  if (details.old_values || details['old values']) {
    const oldVals = details.old_values || details['old values'];
    result.oldValues = typeof oldVals === 'string' ? tryParseJSON(oldVals) : oldVals;
  }

  if (details.new_values || details['new values']) {
    const newVals = details.new_values || details['new values'];
    result.newValues = typeof newVals === 'string' ? tryParseJSON(newVals) : newVals;
  }

  if (details.metadata) {
    result.metadata = typeof details.metadata === 'string' ? tryParseJSON(details.metadata) : details.metadata;
  }

  // Crear lista de cambios formateados
  if (result.changedFields && result.oldValues && result.newValues) {
    result.parsedChanges = result.changedFields
      .filter((field: string) => field !== 'updated_at') // Ignorar updated_at
      .map((field: string) => ({
        field,
        fieldLabel: formatFieldName(field),
        oldValue: formatValue(result.oldValues[field], field),
        newValue: formatValue(result.newValues[field], field)
      }));
  }

  return result;
}

/**
 * Intenta parsear un string JSON de forma segura
 */
function tryParseJSON(jsonString: string): any {
  try {
    return JSON.parse(jsonString);
  } catch {
    return jsonString;
  }
}

/**
 * Genera una descripción legible de la acción de auditoría
 */
export function generateActionDescription(
  action: string,
  details?: Record<string, any>,
  userName?: string
): string {
  const actionType = getActionType(action, details);
  const parsed = details ? parseAuditDetails(details) : null;

  // Si tenemos cambios parseados, crear descripción detallada
  if (parsed && parsed.parsedChanges.length > 0) {
    const mainChange = parsed.parsedChanges[0];

    if (parsed.parsedChanges.length === 1) {
      return `${userName || 'Usuario'} actualizó ${mainChange.fieldLabel.toLowerCase()}`;
    } else {
      return `${userName || 'Usuario'} actualizó ${mainChange.fieldLabel.toLowerCase()} y ${parsed.parsedChanges.length - 1} campo${parsed.parsedChanges.length > 2 ? 's' : ''} más`;
    }
  }

  // Descripciones por tipo de acción
  const userPrefix = userName ? `${userName} ` : '';

  switch (actionType) {
    case 'insert':
      return parsed?.tableName
        ? `${userPrefix}creó ${formatFieldName(parsed.tableName).toLowerCase()}`
        : `${userPrefix}creó un registro`;
    case 'update':
      return parsed?.tableName
        ? `${userPrefix}actualizó ${formatFieldName(parsed.tableName).toLowerCase()}`
        : `${userPrefix}actualizó el registro`;
    case 'delete':
      return parsed?.tableName
        ? `${userPrefix}eliminó ${formatFieldName(parsed.tableName).toLowerCase()}`
        : `${userPrefix}eliminó un registro`;
    case 'assign':
      return `${userPrefix}realizó una asignación`;
    case 'complete':
      return `${userPrefix}completó la tarea`;
    case 'upload':
      return `${userPrefix}cargó una evidencia`;
    default:
      return action;
  }
}

/**
 * Genera un resumen detallado de la acción con contexto adicional
 */
export function generateDetailedSummary(
  action: string,
  details?: Record<string, any>,
  userName?: string
): {
  title: string;
  description: string;
  items: Array<{ icon: string; label: string; value: string; highlight?: boolean }>;
} {
  const actionType = getActionType(action, details);
  const parsed = details ? parseAuditDetails(details) : null;
  const items: Array<{ icon: string; label: string; value: string; highlight?: boolean }> = [];

  let title = generateActionDescription(action, details, userName);
  let description = '';

  // Agregar información del cambio principal
  if (parsed && parsed.parsedChanges.length > 0) {
    const mainChange = parsed.parsedChanges[0];

    // Título más descriptivo según el campo
    if (mainChange.field === 'status') {
      title = `${userName || 'Usuario'} cambió el estado`;
      description = `El estado pasó de "${mainChange.oldValue}" a "${mainChange.newValue}"`;
    } else if (mainChange.field === 'due_date') {
      title = `${userName || 'Usuario'} modificó la fecha de vencimiento`;
      description = `Se ajustó el plazo de cumplimiento`;
    } else if (mainChange.field === 'priority') {
      title = `${userName || 'Usuario'} cambió la prioridad`;
      description = `La prioridad se actualizó de "${mainChange.oldValue}" a "${mainChange.newValue}"`;
    } else if (mainChange.field === 'assignee_id') {
      title = `${userName || 'Usuario'} reasignó el responsable`;
      description = `Se cambió el responsable del control`;
    }

    // Agregar todos los cambios a los items
    parsed.parsedChanges.forEach((change, index) => {
      items.push({
        icon: '📝',
        label: change.fieldLabel,
        value: `${change.oldValue} → ${change.newValue}`,
        highlight: index === 0 // Resaltar el cambio principal
      });
    });
  }

  // Agregar información de tabla si está disponible
  if (parsed?.tableName) {
    const tableLabel = formatFieldName(parsed.tableName);
    items.push({
      icon: '📋',
      label: 'Tipo de registro',
      value: tableLabel
    });
  }

  // Agregar información de metadata si está disponible
  if (parsed?.metadata) {
    if (parsed.metadata.control_code) {
      items.push({
        icon: '🎯',
        label: 'Control',
        value: parsed.metadata.control_code
      });
    }
    if (parsed.metadata.control_title) {
      items.push({
        icon: '📄',
        label: 'Título del control',
        value: parsed.metadata.control_title
      });
    }
    if (parsed.metadata.area || parsed.metadata.unit) {
      items.push({
        icon: '🏢',
        label: 'Área',
        value: parsed.metadata.area || parsed.metadata.unit
      });
    }
  }

  // Detectar eventos específicos basados en el tipo de tabla y acción

  // DETECCIÓN DE CREACIÓN DE TAREA
  if (parsed?.tableName === 'tasks' && actionType === 'insert') {
    title = `${userName || 'Usuario'} creó una nueva tarea`;
    description = 'Se agregó una tarea al control para seguimiento de implementación';

    // Extraer información de la tarea desde new_values o metadata
    const taskData = parsed.newValues || parsed.metadata || {};

    if (taskData.title || taskData.task_title) {
      items.push({
        icon: '📝',
        label: 'Título de la tarea',
        value: taskData.title || taskData.task_title,
        highlight: true
      });
    }

    if (taskData.description || taskData.task_description) {
      items.push({
        icon: '📋',
        label: 'Descripción',
        value: taskData.description || taskData.task_description
      });
    }

    if (taskData.assignee_name || taskData.assigned_to) {
      items.push({
        icon: '👤',
        label: 'Asignada a',
        value: taskData.assignee_name || taskData.assigned_to
      });
    }

    if (taskData.priority) {
      items.push({
        icon: '🚨',
        label: 'Prioridad',
        value: PRIORITY_LABELS[taskData.priority] || taskData.priority
      });
    }

    if (taskData.due_date) {
      items.push({
        icon: '📅',
        label: 'Fecha límite',
        value: formatDate(taskData.due_date)
      });
    }

    if (taskData.estimated_hours) {
      items.push({
        icon: '⏱️',
        label: 'Tiempo estimado',
        value: `${taskData.estimated_hours} horas`
      });
    }
  }

  // DETECCIÓN DE CARGA DE EVIDENCIA/ARCHIVO
  if (parsed?.tableName === 'evidence' && actionType === 'insert') {
    title = `${userName || 'Usuario'} cargó una evidencia`;
    description = 'Se agregó un archivo como evidencia de cumplimiento del control';

    const evidenceData = parsed.newValues || parsed.metadata || {};

    if (evidenceData.name || evidenceData.evidence_name || evidenceData.file_name) {
      items.push({
        icon: '📎',
        label: 'Nombre del archivo',
        value: evidenceData.name || evidenceData.evidence_name || evidenceData.file_name,
        highlight: true
      });
    }

    if (evidenceData.type || evidenceData.evidence_type) {
      const typeLabels: Record<string, string> = {
        document: '📄 Documento',
        screenshot: '🖼️ Captura de pantalla',
        video: '🎥 Video',
        other: '📁 Otro'
      };
      items.push({
        icon: '🗂️',
        label: 'Tipo de evidencia',
        value: typeLabels[evidenceData.type || evidenceData.evidence_type] || evidenceData.type || evidenceData.evidence_type
      });
    }

    if (evidenceData.size || evidenceData.file_size) {
      const size = evidenceData.size || evidenceData.file_size;
      const sizeMB = typeof size === 'number' ? (size / 1024 / 1024).toFixed(2) : size;
      items.push({
        icon: '💾',
        label: 'Tamaño',
        value: `${sizeMB} MB`
      });
    }

    if (evidenceData.uploaded_by) {
      items.push({
        icon: '👤',
        label: 'Subido por',
        value: evidenceData.uploaded_by
      });
    }
  }

  // DETECCIÓN DE ASIGNACIÓN DE REFERENTE/RESPONSABLE
  if (parsed?.tableName === 'control_assignments' && actionType === 'insert') {
    title = `${userName || 'Usuario'} asignó un referente técnico`;
    description = 'Se asignó un responsable para la implementación y seguimiento del control';

    const assignmentData = parsed.newValues || parsed.metadata || {};

    if (assignmentData.referent_name || assignmentData.assigned_to) {
      items.push({
        icon: '👤',
        label: 'Referente asignado',
        value: assignmentData.referent_name || assignmentData.assigned_to,
        highlight: true
      });
    }

    if (assignmentData.referent_email) {
      items.push({
        icon: '📧',
        label: 'Email',
        value: assignmentData.referent_email
      });
    }

    if (assignmentData.unit || assignmentData.area) {
      items.push({
        icon: '🏢',
        label: 'Unidad/Área',
        value: assignmentData.unit || assignmentData.area
      });
    }

    if (assignmentData.assigned_by) {
      items.push({
        icon: '👨‍💼',
        label: 'Asignado por',
        value: assignmentData.assigned_by
      });
    }

    if (assignmentData.assigned_at) {
      items.push({
        icon: '📅',
        label: 'Fecha de asignación',
        value: formatDate(assignmentData.assigned_at)
      });
    }
  }

  // DETECCIÓN DE COMPLETADO DE TAREA
  if (parsed?.tableName === 'tasks' && parsed.parsedChanges.some(c => c.field === 'status' && c.newValue.includes('Completad'))) {
    title = `${userName || 'Usuario'} completó una tarea`;
    description = 'La tarea fue marcada como completada exitosamente';

    const taskData = parsed.metadata || {};

    if (taskData.task_title || taskData.title) {
      items.push({
        icon: '✅',
        label: 'Tarea completada',
        value: taskData.task_title || taskData.title,
        highlight: true
      });
    }

    if (taskData.completed_at) {
      items.push({
        icon: '📅',
        label: 'Completada el',
        value: formatDate(taskData.completed_at)
      });
    }

    if (taskData.actual_hours && taskData.estimated_hours) {
      const diff = taskData.estimated_hours - taskData.actual_hours;
      const diffText = diff > 0
        ? `${diff} horas menos que lo estimado ⚡`
        : diff < 0
        ? `${Math.abs(diff)} horas más que lo estimado`
        : 'Exactamente como se estimó 🎯';

      items.push({
        icon: '⏱️',
        label: 'Tiempo real vs estimado',
        value: `${taskData.actual_hours}h de ${taskData.estimated_hours}h (${diffText})`
      });
    }
  }

  // Descripciones específicas por tipo de acción sin cambios parseados
  if (!parsed || parsed.parsedChanges.length === 0) {
    switch (actionType) {
      case 'insert':
        description = 'Se creó un nuevo registro en el sistema';
        break;
      case 'update':
        description = 'Se realizaron modificaciones en el registro';
        break;
      case 'delete':
        description = 'El registro fue eliminado del sistema';
        break;
      case 'assign':
        description = 'Se asignó un responsable o recurso';
        break;
      case 'complete':
        description = 'La tarea fue marcada como completada';
        break;
      case 'upload':
        description = 'Se cargó un archivo o evidencia';
        break;
    }
  }

  return {
    title,
    description,
    items
  };
}

/**
 * Agrupa entradas de auditoría por periodo temporal
 */
export function groupAuditEntriesByTime(entries: AuditEntry[]): {
  label: string;
  entries: AuditEntry[];
}[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const monthAgo = new Date(today.getTime() - 30 * 86400000);

  const groups: Record<string, AuditEntry[]> = {
    'Hoy': [],
    'Ayer': [],
    'Esta semana': [],
    'Este mes': [],
    'Más antiguo': []
  };

  entries.forEach(entry => {
    const entryDate = new Date(entry.timestamp);

    if (entryDate >= today) {
      groups['Hoy'].push(entry);
    } else if (entryDate >= yesterday) {
      groups['Ayer'].push(entry);
    } else if (entryDate >= weekAgo) {
      groups['Esta semana'].push(entry);
    } else if (entryDate >= monthAgo) {
      groups['Este mes'].push(entry);
    } else {
      groups['Más antiguo'].push(entry);
    }
  });

  // Filtrar grupos vacíos y convertir a array
  return Object.entries(groups)
    .filter(([_, entries]) => entries.length > 0)
    .map(([label, entries]) => ({ label, entries }));
}
